import React, { useEffect, useState } from 'react';
import { CSSTransition } from 'react-transition-group';
import { classes, useSlug } from '@bloomberg/media-ui-core';
import { ChevronDown } from '@bloomberg/media-ui-icons';
import FocusTrap from 'focus-trap-react';

import { MARKET_DATA_DROPDOWN_MENU_IDS } from '../constants/constants';
import { DropdownMenuDesktop } from '../DropdownMenuDesktop/index';
import { getLiveUrl } from '../i18n';
import { MarketDataDropdown } from '../MarketDataDropdown/index';
import { RegionPicker } from '../RegionPicker/index';
import {
  MarketDataSubmenuItems,
  MenuDesktopProps,
  MenuItem as MenuItemType,
  SubmenuItem,
  THEMES,
} from '../types';
import {
  TranslatedMessage,
  useTranslateMessage,
} from '../utils/translate-messages';

import styles from './MenuDesktop.module.css';

const TOTAL_DELAY_TIME = 0;
const RENDERING_DELAY_TIME = 300;

type MenuItemProps = {
  id: string;
  text: string;
  submenuItems?: Record<string, SubmenuItem>;
  trackingFunction: (action: string, eventLabel: string) => void;
  onKeyDown: (event: React.KeyboardEvent) => void;
};

const MenuItem = ({
  id,
  text,
  submenuItems,
  trackingFunction,
  onKeyDown,
}: MenuItemProps) => {
  const url = submenuItems && submenuItems[`metadata_${id}`]?.url;
  const dropdownListItems =
    submenuItems && submenuItems[`metadata_${id}`]?.dropdownListItems;

  if (!url)
    return (
      <>
        {text}
        {submenuItems && Object.keys(submenuItems).length > 0 && (
          <div data-testid="chevron-arrow" tabIndex={0} onKeyDown={onKeyDown}>
            <ChevronDown />
          </div>
        )}
      </>
    );

  return (
    <>
      <a href={url} onClick={() => trackingFunction('click', text)}>
        {text}
      </a>
      {dropdownListItems && dropdownListItems.length > 0 && (
        <div data-testid="chevron-arrow" tabIndex={0} onKeyDown={onKeyDown}>
          <ChevronDown />
        </div>
      )}
    </>
  );
};

const DelayWrapper = ({
  children,
  isOpen,
}: {
  children: React.ReactNode;
  isOpen: boolean;
}) => {
  const [isShown, setIsShown] = useState(false);

  useEffect(() => {
    // this will block rendering of the component for RENDERING_DELAY_TIME amount
    if (!isOpen) {
      setIsShown(false);

      return undefined;
    }

    const timer = setTimeout(() => {
      setIsShown(true);
    }, RENDERING_DELAY_TIME);

    return () => clearTimeout(timer);
  }, [RENDERING_DELAY_TIME, isOpen]);

  return isShown ? (
    <CSSTransition
      in={isOpen}
      mountOnEnter
      timeout={TOTAL_DELAY_TIME}
      classNames={{
        enter: styles.menuItemsEnter,
        enterActive: styles.menuItemsEnterActive,
      }}
      unmountOnExit
    >
      {children}
    </CSSTransition>
  ) : null;
};

export const MenuDesktop = React.forwardRef<HTMLDivElement, MenuDesktopProps>(
  function MenuDesktopComponent(props: MenuDesktopProps, ref) {
    const {
      onMenuItemActive,
      marketDataTickersUrl,
      menuItems,
      theme,
      region,
      desktopBreakpointClassName,
      trackingFunction,
      locale,
    } = props;

    const [activeMenuItemId, setActiveMenuItemId] = useState<string | null>(
      null
    );
    const [isRegionPickerOpen, setIsRegionPickerOpen] =
      useState<boolean>(false);
    const translateMessage = useTranslateMessage();

    useEffect(() => {
      if (isRegionPickerOpen) {
        setActiveMenuItemId(null);
      }
    }, [isRegionPickerOpen]);

    useEffect(() => {
      if (activeMenuItemId) {
        setIsRegionPickerOpen(false);
        if (onMenuItemActive) {
          onMenuItemActive();
        }
      }
    }, [activeMenuItemId]);

    const onKeyDown = (event: React.KeyboardEvent, id: string) => {
      if (event.key !== 'Enter' && event.key !== ' ') {
        return;
      }

      event.preventDefault();

      if (activeMenuItemId === id) {
        setActiveMenuItemId(null);

        return;
      }

      setActiveMenuItemId(id);
    };

    const onMouseOver: React.MouseEventHandler<HTMLDivElement> = (event) => {
      const el = event.target as HTMLDivElement;
      const id = el.dataset.menuid as string;
      const submenuId = el.dataset.submenuid as string;

      if (!id || submenuId) {
        return;
      }

      setActiveMenuItemId(id);
    };

    const onMouseLeave: React.MouseEventHandler<HTMLDivElement> = () => {
      setActiveMenuItemId(null);
    };

    const componentName = useSlug('menu-desktop');
    const liveUrl = getLiveUrl(locale);

    const cx = classes([
      styles.menuDesktopContainer,
      {
        [styles.light]: theme === THEMES.LIGHT,
        [styles.dark]: theme === THEMES.DARK,
        [styles.blue]: theme === THEMES.BLUE,
      },
    ]);

    if (!menuItems || !Object.keys(menuItems).length) {
      return null;
    }

    const activeMenuItem = activeMenuItemId && menuItems[activeMenuItemId];
    const showMarketDataDropdown = Boolean(
      activeMenuItem && MARKET_DATA_DROPDOWN_MENU_IDS.has(activeMenuItemId)
    );

    const marketDataSubmenu = showMarketDataDropdown
      ? ((activeMenuItem as MenuItemType)
          .submenuItems as MarketDataSubmenuItems)
      : null;
    const showDropdownMenuDesktop = Boolean(
      activeMenuItem &&
        !activeMenuItem.useSubmenu &&
        !MARKET_DATA_DROPDOWN_MENU_IDS.has(activeMenuItemId)
    );

    return (
      <FocusTrap
        active={Boolean(activeMenuItem)}
        focusTrapOptions={{
          fallbackFocus: `body`,
          clickOutsideDeactivates: true,
          escapeDeactivates: true,
          initialFocus: false,
          returnFocusOnDeactivate: false,
        }}
      >
        <div
          className={cx}
          ref={ref}
          aria-label={translateMessage({
            id: 'media-ui-nav_arialabel_menu',
            defaultMessage: 'Menu',
          })}
          data-testid="menu-desktop"
          data-component={componentName}
          onMouseOver={onMouseOver}
          onMouseLeave={onMouseLeave}
        >
          <div className={classes([styles.bar, desktopBreakpointClassName])}>
            <div className={styles.menuItems}>
              <a
                href={liveUrl}
                className={classes([styles.menuItem, styles.liveTVMenuItem])}
                data-menuid="live-now"
                onClick={() => trackingFunction('click', 'LiveTV')}
              >
                <span className={styles.liveNow}>
                  <TranslatedMessage
                    id="media-ui-nav_liveTv"
                    defaultMessage="Live TV"
                  />
                </span>
              </a>
              <ul className={styles.menuItemsContainer}>
                {Object.values(menuItems).map(({ id, text, submenuItems }) => (
                  <li
                    className={classes([
                      styles.menuItem,
                      activeMenuItemId === id && styles.active,
                    ])}
                    key={id}
                    aria-label={text}
                    data-menuid={id}
                  >
                    <MenuItem
                      id={id}
                      text={text}
                      submenuItems={submenuItems}
                      trackingFunction={trackingFunction}
                      onKeyDown={(e) => onKeyDown(e, id)}
                    />
                  </li>
                ))}
              </ul>
            </div>
            <div className={styles.pickers}>
              <RegionPicker
                trackingFunction={trackingFunction}
                region={region}
                isRegionPickerOpen={isRegionPickerOpen}
                setIsRegionPickerOpen={setIsRegionPickerOpen}
                theme={theme}
              />
            </div>
          </div>
          <DelayWrapper
            isOpen={showDropdownMenuDesktop || showMarketDataDropdown}
          >
            <div className={styles.dropdown}>
              {showDropdownMenuDesktop && (
                <DropdownMenuDesktop
                  marketDataTickersUrl={marketDataTickersUrl}
                  menuItemId={(activeMenuItem as MenuItemType).id}
                  submenuItems={(activeMenuItem as MenuItemType).submenuItems}
                  items={(activeMenuItem as any).dropdownListItems}
                  theme={theme}
                  desktopBreakpointClassName={desktopBreakpointClassName}
                  trackingFunction={(
                    submenuText: string,
                    dropdownListItemText: string
                  ) => {
                    trackingFunction(
                      'click',
                      `${
                        (activeMenuItem as MenuItemType).text
                      }-${submenuText}-${dropdownListItemText}`
                    );
                  }}
                />
              )}
              {showMarketDataDropdown && marketDataSubmenu && (
                <div className={styles.marketDataContainer}>
                  <MarketDataDropdown
                    desktopBreakpointClassName={desktopBreakpointClassName}
                    marketDataTickersUrl={marketDataTickersUrl}
                    marketDataLinks={marketDataSubmenu.market_data_links}
                    marketDataNews={marketDataSubmenu.news_links}
                    tickers={
                      marketDataSubmenu.market_data_tickers.dropdownListItems
                    }
                    trackingFunction={trackingFunction}
                  />
                </div>
              )}
            </div>
          </DelayWrapper>
        </div>
      </FocusTrap>
    );
  }
);
